import React, { useState } from "react";
import { Button, Modal, Form, InputNumber, Input, message } from "antd";
import { CreditCardOutlined } from "@ant-design/icons";
import { useCallback } from "react";
import facturaApi from "../../modules/admin/facturas/store/api";
import clientBillingApi from "../../modules/client/billing/store/api";

const { TextArea } = Input;

const PayPartialButton = ({
  facturaId,
  totalAmount,
  paidAmount = 0,
  onSuccess,
  onError,
  disabled = false,
  size = "small",
  isClientSide = false,
  facturaStatus,
  className = ""
}) => {
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  const remainingBalance = totalAmount - paidAmount;

  const handleShowModal = useCallback(
    (e) => {
      e.stopPropagation();
      setModalVisible(true);
      form.setFieldsValue({
        partialAmount: remainingBalance,
        note: ""
      });
    },
    [remainingBalance, form]
  );

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
  };

  const handlePayPartial = useCallback(
    async (values) => {
      if (loading) return;

      const { partialAmount, note } = values;

      if (partialAmount <= 0) {
        message.error("Payment amount must be greater than zero");
        return;
      }

      if (partialAmount > remainingBalance) {
        message.error(
          `Payment amount cannot exceed remaining balance of $${remainingBalance.toFixed(
            2
          )}`
        );
        return;
      }

      setLoading(true);

      try {
        const api = isClientSide ? clientBillingApi : facturaApi;
        const result = await api.payPartialInvoice(facturaId, partialAmount, note);

        if (result.success) {
          message.success("Partial payment successful!");
          setModalVisible(false);
          form.resetFields();
          if (onSuccess) {
            onSuccess(result);
          }
        } else {
          throw new Error("Payment was not successful");
        }
      } catch (error) {
        console.error("Payment error:", error);
        message.error(`Payment failed: ${error.message}`);
        if (onError) {
          onError(error);
        }
      } finally {
        setLoading(false);
      }
    },
    [facturaId, loading, remainingBalance, onSuccess, onError, isClientSide, form]
  );

  // Don't show button for paid invoices - moved after hooks
  if (facturaStatus === "paid" || remainingBalance <= 0) {
    return null;
  }

  return (
    <>
      <Button
        size={size}
        icon={<CreditCardOutlined />}
        disabled={disabled}
        onClick={handleShowModal}
        className={`pay-partial-button ${className}`}
        style={{
          backgroundColor: "#faad14",
          borderColor: "#faad14",
          color: "white"
        }}
      >
        Pay Partial
      </Button>

      <Modal
        title="Pay Partial Amount"
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handlePayPartial}>
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              backgroundColor: "#f5f5f5",
              borderRadius: 6
            }}
          >
            <div>
              <strong>Invoice Total:</strong> ${totalAmount.toFixed(2)}
            </div>
            <div>
              <strong>Amount Paid:</strong> ${paidAmount.toFixed(2)}
            </div>
            <div>
              <strong>Remaining Balance:</strong> ${remainingBalance.toFixed(2)}
            </div>
          </div>

          <Form.Item
            name="partialAmount"
            label="Partial Payment Amount ($)"
            rules={[
              { required: true, message: "Please enter a payment amount" },
              {
                validator: (_, value) => {
                  if (value <= 0) {
                    return Promise.reject(new Error("Amount must be greater than zero"));
                  }
                  if (value > remainingBalance) {
                    return Promise.reject(
                      new Error(
                        `Amount cannot exceed remaining balance of $${remainingBalance.toFixed(
                          2
                        )}`
                      )
                    );
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0.01}
              max={remainingBalance}
              precision={2}
              formatter={(value) =>
                value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
              }
              parser={(value) => value?.replace(/(,*)/g, "") || ""}
            />
          </Form.Item>

          <Form.Item
            name="note"
            label="Note"
            rules={[
              { required: true, message: "Please enter a note for this partial payment" }
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Enter a note describing this partial payment (required)..."
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button onClick={handleCancel} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{
                backgroundColor: "#faad14",
                borderColor: "#faad14"
              }}
            >
              {loading ? "Processing..." : "Pay Partial Amount"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PayPartialButton;
